<template>
  <v-card
    data-test-id="monthlyUsage"
    class="card"
    elevation="0"
    variant="outlined"
    :title="t('headers.totalFitTestsPerformed', { total })"
    prepend-icon="mdi-chart-bar"
    width="100%"
  >
    <highcharts min-width="100px" :options="chartOptions" />
  </v-card>
</template>

<script setup lang="ts">
// STORES, IMPORTS, & COMPOSABLES
import type { ISeries } from '@/interfaces/ISeries'
import type { IUsage } from '@/interfaces/IUsage'
import { sum } from 'lodash-es'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })

// PROPS & EMITS

// REACTIVE VARIABLES
const model = defineModel<IUsage>({ default: {} })
const chartOptions = ref<Record<string, any>>({
  chart: {
    type: 'column'
  },
  credits: {
    enabled: false
  },
  title: {
    text: undefined
  },
  xAxis: {
    categories: []
  },
  yAxis: {
    min: 0,
    title: {
      text: t('labels.total')
    },
    stackLabels: {
      enabled: true
    }
  },
  tooltip: {
    headerFormat: '<b>{category}</b><br/>',
    pointFormat: '{series.name}: {point.y}'
  },
  plotOptions: {
    column: {
      stacking: 'normal',
      dataLabels: {
        enabled: true
      }
    }
  },
  series: []
})

// COMPUTED PROPERTIES
const total = computed(() => {
  let result = 0
  chartOptions.value.series.forEach((series: ISeries) => {
    result += sum(series.data)
  })
  return result
})

// LIFECYCLE HOOKS

// WATCHERS
watch(
  model,
  (value) => {
    chartOptions.value.xAxis.categories = value.categories
    chartOptions.value.series = value.series
  },
  { immediate: true }
)

// FUNCTIONS
</script>

<style lang="scss" scoped>
.card {
  border-color: $color-dark-16;
  margin: 0;
  padding: 0;
}
.highcharts-figure,
.highcharts-data-table table {
  min-width: 310px;
  width: 100%;
  margin: 1em auto;
}
</style>
